import { Suspense, lazy, useEffect } from "react";

import { useStagedBoolean } from "~/hooks/useStagedBoolean";

import Spinner from "../spinner/Spinner";
import type { JsonEditorProps } from "./JsonEditor";
import styles from "./JsonEditorLazy.module.scss";

const JsonEditor = lazy(() => import("./JsonEditor"));

// Keep in sync with the JsonEditor minHeight default. Only the type may be
// imported from ./JsonEditor here — importing a value would statically pull
// CodeMirror into the main bundle and defeat the code splitting.
const DEFAULT_MIN_HEIGHT = "10rem";

const SPINNER_DELAY_MS = 150;

export type JsonEditorLazyProps = JsonEditorProps & {
  /** Reports while the editor chunk is being fetched (the fallback is
   * mounted), so callers can disable actions until editing is possible. */
  onLoadingChange?: (isLoading: boolean) => void;
};

const JsonEditorFallback = ({
  minHeight,
  onLoadingChange,
}: {
  minHeight: string;
  onLoadingChange?: (isLoading: boolean) => void;
}) => {
  // Delay the spinner so it doesn't flash when the chunk is already cached
  // and the fallback lives for just a frame or two.
  const showSpinner = useStagedBoolean(true, SPINNER_DELAY_MS);

  useEffect(() => {
    onLoadingChange?.(true);

    return () => onLoadingChange?.(false);
  }, [onLoadingChange]);

  return (
    <div
      className={styles.fallback}
      style={{ minHeight }}
      data-testid="json-editor-loading"
    >
      {showSpinner && <Spinner size="2rem" />}
    </div>
  );
};

// Lazy wrapper so CodeMirror lands in its own async chunk, fetched only when
// a JSON editor is actually rendered. Until the chunk streams in, an
// editor-sized placeholder is shown so the swap doesn't jump.
const JsonEditorLazy = ({ onLoadingChange, ...props }: JsonEditorLazyProps) => (
  <Suspense
    fallback={
      <JsonEditorFallback
        minHeight={props.minHeight ?? DEFAULT_MIN_HEIGHT}
        onLoadingChange={onLoadingChange}
      />
    }
  >
    <JsonEditor {...props} />
  </Suspense>
);

export default JsonEditorLazy;
